import { useEffect, useState } from 'react';
import { Radio, Button } from '@material-tailwind/react';
import { useInspectionDetailStore } from '@stores';
import { formatName } from '@utils/formatters';
import {
  DETAILS_DRIVER_LICENSE_FRONT,
  DETAILS_DRIVER_LICENSE_BACK,
  DETAILS_DRIVER_LICENSE_SELFIE,
  DETAILS_BANK,
  DETAILS_CERTIFICATE,
  DETAILS_REGO_PHOTO_BACK,
  DETAILS_REGO_PHOTO_FRONT,
  DETAILS_VEHICLE_ASSET_BACK,
  DETAILS_VEHICLE_ASSET_FRONT,
  DETAILS_PAYOUT_LETTER,
  DETAILS_OWNERSHIP_PROOF,
  DETAILS_VEHICLE_DRIVER_SIDE,
  DETAILS_VEHICLE_ODOMETER,
  DETAILS_VEHICLE_PASSENGER_SIDE,
  DETAILS_VEHICLE_PLATE,
  DETAILS_VEHICLE_VIN,
  DETAILS_MARINE_HIN,
  DETAILS_MARINE_REGO_NUMBER,
  DETAILS_MARINE_REGO_CERT,
  DETAILS_VEHICLE_VIN_LOCATION,
  DETAILS_VEHICLE_PLATE_LOCATION,
  DETAILS_VEHICLE_INTERNAL_FRONT,
  DETAILS_VEHICLE_INTERNAL_BACK,
  DETAILS_VEHICLE_INTERNAL_BOOT,
  DETAILS_VEHICLE_INTERNAL_LIVING_AREA,
  DETAILS_VEHICLE_INTERNAL_KITCHEN,
  DETAILS_VEHICLE_INTERNAL_SLEEPING_AREA,
  DOCUMENT_TYPE
} from '@constants';
import {
  DocumentFormEnum,
  DocumentFormType,
  LenderDocumentTypeEnum
} from '@types';
import _get from 'lodash/get';
import _map from 'lodash/map';
import { format } from 'date-fns';
import DriverLicense from '/images/card_front.png';
import VehicleImg from '/images/vehicle.png';
import RegoImg from '/images/rego.png';
import BankImg from '/images/bank.png';
import CertImg from '/images/cert.png';
import { post, GET_INSPECTION_FILE } from '@services';
import { useParams } from 'react-router-dom';
import Loader from '../../../../components/widgets/Loader';
import _groupBy from 'lodash/groupBy';
import _maxBy from 'lodash/maxBy';
import _flatMap from 'lodash/flatMap';
import EnlargeableImage from '@components/widgets/EnlargeableImage';

const DOCUMENT_IMAGE_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    DOCUMENT_TYPE.map(({ value, label }) => [value, label])
  ),
  [LenderDocumentTypeEnum.DRIVER_LICENCE_FRONT]: 'Driver licence front',
  [LenderDocumentTypeEnum.DRIVER_LICENCE_BACK]: 'Driver licence back',
  [LenderDocumentTypeEnum.FACE]: 'Selfie',
  [LenderDocumentTypeEnum.SAFETY_CERT]: 'Safety certificate'
};

interface IProps {
  type: DocumentFormType;
}

const DocumentCard = ({ type }: IProps) => {
  const {
    activePanel,
    isCollapsed,
    setCollapse,
    inspectionDetailData,
    activeStep
  } = useInspectionDetailStore();
  const isDemo = location.pathname.includes('/demo/inspection');
  const formattedDate = (date: any) => format(date, 'dd/MM/yyyy h:mma');
  const [verifyStatus, setVerifyStatus] = useState<string>();
  const [formElements, setFormElements] = useState<any>([]);
  const [specialFormElements, setSpecialFormElements] = useState<any>([]);
  const [seller, setSeller] = useState<any>([]);
  const [isLoading, setLoading] = useState(true);
  const [imgsrc, setImgSrc] = useState<any>();
  const param = useParams();
  const [selectedFile, setSelectedFile] = useState<any>();

  const handleClose = () => {
    setCollapse(true);
  };
  const getLatestFiles = (files: any) => {
    const groupedFiles = _groupBy(files, 'documentType');

    const latestFiles = _flatMap(groupedFiles, (group) =>
      _maxBy(group, 'createdTime')
    );

    return latestFiles;
  };
  const files = getLatestFiles(inspectionDetailData.files);
  useEffect(() => {
    let fileTypes = [];
    switch (type) {
      case DocumentFormEnum.DETAILS_DRIVER_LICENSE_FRONT:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'DRIVER_LICENCE_FRONT'
        );
        setFormElements(DETAILS_DRIVER_LICENSE_FRONT);
        break;
      case DocumentFormEnum.DETAILS_DRIVER_LICENSE_BACK:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'DRIVER_LICENCE_BACK'
        );
        setFormElements(DETAILS_DRIVER_LICENSE_BACK);
        break;
      case DocumentFormEnum.DETAILS_DRIVER_LICENSE_SELFIE:
        fileTypes = files.filter((e: any) => e.documentType === 'FACE');
        setFormElements(DETAILS_DRIVER_LICENSE_SELFIE);
        break;
      case DocumentFormEnum.DETAILS_BANK:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'BANK_STATEMENTS'
        );
        setFormElements(DETAILS_BANK);
        break;
      case DocumentFormEnum.DETAILS_CERTIFICATE:
        fileTypes = files.filter(
          (e: any) =>
            e.documentType === 'ROADWORTHY_CERT' ||
            e.documentType === 'SAFETY_CERT'
        );
        setFormElements(DETAILS_CERTIFICATE);
        break;
      case DocumentFormEnum.DETAILS_PAYOUT_LETTER:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'PAYOUT_LETTER'
        );
        setFormElements(DETAILS_PAYOUT_LETTER);
        break;
      case DocumentFormEnum.DETAILS_OWNERSHIP_PROOF:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'OWNERSHIP_PROOF'
        );
        setFormElements(DETAILS_OWNERSHIP_PROOF);
        break;
      case DocumentFormEnum.DETAILS_REGO_PHOTO_BACK:
        fileTypes = files.filter((e: any) => e.documentType === 'REGO_PAPERS');
        setFormElements(DETAILS_REGO_PHOTO_BACK);
        break;
      case DocumentFormEnum.DETAILS_REGO_PHOTO_FRONT:
        fileTypes = files.filter((e: any) => e.documentType === 'REGO_PAPERS');
        setFormElements(DETAILS_REGO_PHOTO_FRONT);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_ASSET_BACK:
        fileTypes = files.filter((e: any) => e.documentType === 'ASSET_BACK');
        setFormElements(DETAILS_VEHICLE_ASSET_BACK);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_ASSET_FRONT:
        fileTypes = files.filter((e: any) => e.documentType === 'ASSET_FRONT');
        setFormElements(DETAILS_VEHICLE_ASSET_FRONT);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_DRIVER_SIDE:
        fileTypes = files.filter((e: any) => e.documentType === 'ASSET_RIGHT');
        setFormElements(DETAILS_VEHICLE_DRIVER_SIDE);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_INTERNAL_FRONT:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'INTERNAL_FRONT'
        );
        setFormElements(DETAILS_VEHICLE_INTERNAL_FRONT);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_INTERNAL_BACK:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'INTERNAL_BACK'
        );
        setFormElements(DETAILS_VEHICLE_INTERNAL_BACK);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_INTERNAL_BOOT:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'INTERNAL_BOOT'
        );
        setFormElements(DETAILS_VEHICLE_INTERNAL_BOOT);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_INTERNAL_LIVING_AREA:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'INTERNAL_LIVING_AREA'
        );
        setFormElements(DETAILS_VEHICLE_INTERNAL_LIVING_AREA);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_INTERNAL_KITCHEN:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'INTERNAL_KITCHEN'
        );
        setFormElements(DETAILS_VEHICLE_INTERNAL_KITCHEN);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_INTERNAL_SLEEPING_AREA:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'INTERNAL_SLEEPING_AREA'
        );
        setFormElements(DETAILS_VEHICLE_INTERNAL_SLEEPING_AREA);
        break;

      case DocumentFormEnum.DETAILS_VEHICLE_ODOMETER:
        fileTypes = files.filter((e: any) => e.documentType === 'ODOMETER');
        setFormElements(DETAILS_VEHICLE_ODOMETER);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_OPERATING_HOURS:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'OPERATING_HOURS'
        );
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_PASSENGER_SIDE:
        fileTypes = files.filter((e: any) => e.documentType === 'ASSET_LEFT');
        setFormElements(DETAILS_VEHICLE_PASSENGER_SIDE);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_PLATE:
        fileTypes = files.filter((e: any) => e.documentType === 'REGO_PLATE');
        setFormElements(DETAILS_VEHICLE_PLATE_LOCATION);
        setSpecialFormElements(DETAILS_VEHICLE_PLATE);
        break;
      case DocumentFormEnum.DETAILS_VEHICLE_VIN:
        fileTypes = files.filter((e: any) => e.documentType === 'VIN');
        setFormElements(DETAILS_VEHICLE_VIN_LOCATION);
        setSpecialFormElements(DETAILS_VEHICLE_VIN);
        break;
      case DocumentFormEnum.DETAILS_MARINE_HIN:
        fileTypes = files.filter((e: any) => e.documentType === 'MARINE_HIN');
        setFormElements(DETAILS_VEHICLE_VIN_LOCATION);
        setSpecialFormElements(DETAILS_MARINE_HIN);
        break;
      case DocumentFormEnum.DETAILS_MARINE_REGO_NUMBER:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'MARINE_REGO_NUMBER'
        );
        setFormElements(DETAILS_VEHICLE_PLATE_LOCATION);
        setSpecialFormElements(DETAILS_MARINE_REGO_NUMBER);
        break;
      case DocumentFormEnum.DETAILS_MARINE_REGO_CERT:
        fileTypes = files.filter(
          (e: any) => e.documentType === 'MARINE_REGO_CERT'
        );
        setFormElements(DETAILS_MARINE_REGO_CERT);
        break;
      case DocumentFormEnum.DETAILS_MARINE_FRONT:
        fileTypes = files.filter((e: any) => e.documentType === 'MARINE_FRONT');
        setFormElements(DETAILS_VEHICLE_ASSET_FRONT);
        break;
      case DocumentFormEnum.DETAILS_MARINE_BACK:
        fileTypes = files.filter((e: any) => e.documentType === 'MARINE_BACK');
        setFormElements(DETAILS_VEHICLE_ASSET_BACK);
        break;
      case DocumentFormEnum.DETAILS_MARINE_LEFT:
        fileTypes = files.filter((e: any) => e.documentType === 'MARINE_LEFT');
        setFormElements(DETAILS_VEHICLE_PASSENGER_SIDE);
        break;
      case DocumentFormEnum.DETAILS_MARINE_RIGHT:
        fileTypes = files.filter((e: any) => e.documentType === 'MARINE_RIGHT');
        setFormElements(DETAILS_VEHICLE_DRIVER_SIDE);
        break;
    }
    if (fileTypes.length > 0) {
      post(
        GET_INSPECTION_FILE,
        { inspectionId: param.id, fileId: fileTypes[0].fileId },
        false
      )
        .then((data) => {
          setLoading(false);
          if (data && data.file && data.file) {
            setSeller(data.file.user);
            if (inspectionDetailData.extraFields) {
              const document =
                inspectionDetailData?.documents[data.file.documentType];
              const extraFields = document?.extraFields;
              data.file.fields2 = extraFields;
            }
            setSelectedFile(data.file);
          }
          if (data && data.dataBase64) {
            setImgSrc(data.dataBase64);
          }
        })
        .catch((e) => {
          setLoading(false);
          console.error(e);
        });
    }
  }, [type, inspectionDetailData.files]);
  if (isLoading) return <Loader />;
  const imageAlt =
    DOCUMENT_IMAGE_LABELS[
      selectedFile?.documentType || activePanel.activeSection
    ] || 'Document';

  return (
    <div className="flex flex-col p-6 gap-6 border-t">
      <div className="flex flex-col gap-x-2 h-full min-h-0">
        <div className="flex flex-col w-full gap-y-2">
          <h1 className="text-grey-800 font-semibold text-base">
            Provided by{' '}
            {seller
              ? formatName(seller.firstName, seller.lastName)
              : inspectionDetailData.seller.firstName +
                ' ' +
                inspectionDetailData.seller.lastName}
          </h1>

          <div className="flex flex-col gap-8">
            {imgsrc && !isDemo && (
              <EnlargeableImage
                src={`data:image/jpeg;base64,${imgsrc}`}
                alt={imageAlt}
              />
            )}
            {isDemo && <EnlargeableImage src={imgsrc} alt={imageAlt} />}
          </div>

          {specialFormElements.length > 0 &&
            _map(
              specialFormElements,
              (row: any, index: number) =>
                _get(selectedFile, row.keyPath) && (
                  <div
                    key={index}
                    className="flex flex-row justify-between border-b py-3.5">
                    <p className="text-grey-500 text-sm leading-5">
                      {row.displayName}
                    </p>
                    <p className="text-base text-grey-700">
                      {_get(selectedFile, row.keyPath)}
                    </p>
                  </div>
                )
            )}

          <div className="flex flex-col justify-center items-center">
            {_map(formElements, (row: any, index: number) => {
              if (row.key === 'location') {
                const hasClientAddress = !!_get(selectedFile, 'clientAddress');
                const hasNetworkAddress = !!_get(
                  selectedFile,
                  'networkAddress'
                );
                if (hasClientAddress) {
                  return (
                    <div key={index} className="flex flex-row gap-x-1">
                      <p className="text-grey-500 text-xs leading-[18px]">
                        {row.displayName + ':'}
                      </p>
                      <p className="text-grey-500 text-xs leading-[18px]">
                        {_get(selectedFile, 'clientAddress.label')}
                      </p>
                    </div>
                  );
                }
                if (hasNetworkAddress) {
                  return (
                    <div
                      key={index}
                      className="w-full flex flex-col items-center">
                      <p className="text-grey-500 text-xs leading-[18px] text-center">
                        Approximate location based on IP address.
                      </p>
                      <div className="flex flex-row gap-x-1 justify-center">
                        <p className="text-grey-500 text-xs leading-[18px]">
                          {row.displayName + ':'}
                        </p>
                        <p className="text-grey-500 text-xs leading-[18px]">
                          {_get(selectedFile, 'networkAddress.label')}
                        </p>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={index}
                    className="w-full flex flex-col items-center">
                    <p className="text-grey-500 text-xs leading-[18px] text-center">
                      Location unavailable.
                    </p>
                  </div>
                );
              }
              if (_get(selectedFile, row.keyPath)) {
                return (
                  <div key={index} className="flex flex-row gap-x-1">
                    <p className="text-grey-500 text-xs leading-[18px]">
                      {row.displayName + ':'}
                    </p>
                    <p className="text-grey-500 text-xs leading-[18px]">
                      {row.type === 'Date'
                        ? formattedDate(_get(selectedFile, row.keyPath))
                        : _get(selectedFile, row.keyPath)}
                    </p>
                  </div>
                );
              }
              return null;
            })}
          </div>

          {activePanel.status === 'Incomplete' && type === 'DETAILS_BANK' && (
            <div className="flex flex-col border-grey-200 border p-1 bg-grey-50 rounded-lg hidden">
              <Radio
                checked={verifyStatus === 'reviewed'}
                onChange={() => setVerifyStatus('reviewed')}
                color="green"
                name="type"
                label="Verified through manual review"
              />
              <Radio
                checked={verifyStatus === 'deny'}
                onChange={() => setVerifyStatus('deny')}
                color="red"
                name="type"
                label="Cannot verify"
              />
              <Radio
                checked={verifyStatus === 'resupply'}
                color="orange"
                onChange={() => setVerifyStatus('resupply')}
                name="type"
                label="Request seller to resupply"
              />
            </div>
          )}
          {!isCollapsed &&
            activePanel.status === 'Incomplete' &&
            type === 'DETAILS_BANK' && (
              <div className="sticky lg:w-full lg:justify-end bottom-0 right-0 w-full bg-white flex flex-col md:flex-row gap-4 mt-6 mb-[60px]">
                <Button
                  className="font-semibold bg-white border-grey-300 text-grey-700 w-full text-base capitalize"
                  variant="outlined"
                  onClick={handleClose}>
                  Close
                </Button>
                <Button className="font-semibold bg-primary w-1/2 text-base capitalize hidden">
                  Submit review
                </Button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
export default DocumentCard;
